import React, { useState, useEffect } from 'react';
import { FaHome, FaBox, FaEnvelope, FaCalculator } from 'react-icons/fa';

const Navbar = ({ currentPage, setPage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'landing', label: 'Home', icon: <FaHome /> },
    { id: 'products', label: 'Products', icon: <FaBox /> },
    { id: 'calculator', label: 'Calculator', icon: <FaCalculator /> },
    { id: 'contact', label: 'Contact', icon: <FaEnvelope /> },
  ];

  const handleNavigation = (pageId) => {
    setPage(pageId);
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="logo" onClick={() => handleNavigation('landing')}>
        <div className="logo-wrapper">
          <img 
            src="/logo.png" 
            alt="Senidek Technologies" 
            className="logo-img" 
            onError={(e) => { 
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="logo-text-wrapper">
          <span className="logo-text">Senidek</span>
          <span className="logo-highlight">Technologies</span>
        </div>
      </div>

      <div className={`nav-links ${isMobileMenuOpen ? 'open' : ''}`}>
        {navItems.map((item) => (
          <button
            key={item.id}
            className={currentPage === item.id ? 'active' : ''}
            onClick={() => handleNavigation(item.id)}
          >
            <span className="nav-icon">{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>

      <button
        className="mobile-menu-btn"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className={`hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      <style>{`
        .navbar {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 2px solid var(--gray-100);
          margin-bottom: 24px;
          position: sticky;
          top: 0;
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          z-index: 1000;
          transition: all 0.3s ease;
          border-radius: 0 0 24px 24px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
        }

        .navbar.scrolled {
          box-shadow: 0 4px 24px rgba(37, 99, 235, 0.12);
          background: rgba(255, 255, 255, 0.98);
          border-bottom-color: var(--primary-light);
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          user-select: none;
          text-decoration: none;
          transition: transform 0.3s ease;
        }

        .logo:hover {
          transform: scale(1.02);
        }

        .logo-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .logo-wrapper:hover {
          transform: rotate(-6deg) scale(1.06);
          box-shadow: 0 6px 24px rgba(37, 99, 235, 0.4);
        }

        .logo-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .logo-text-wrapper {
          display: flex;
          align-items: baseline;
          gap: 6px;
        }

        .logo-text {
          font-size: 1.5rem;
          font-weight: 800;
          letter-spacing: -0.5px;
          color: var(--gray-900);
          transition: color 0.3s ease;
        }

        .logo-highlight {
          font-size: 1.2rem;
          font-weight: 600;
          letter-spacing: -0.3px;
          color: var(--primary);
          transition: color 0.3s ease;
        }

        .nav-links {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }

        .nav-links button {
          background: transparent;
          border: none;
          padding: 10px 22px;
          border-radius: 50px;
          font-weight: 500;
          font-size: 0.95rem;
          color: var(--gray-600);
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
        }

        .nav-links button::after {
          content: '';
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--primary), var(--accent));
          border-radius: 2px;
          transition: width 0.3s ease;
        }

        .nav-links button:hover::after {
          width: 60%;
        }

        .nav-links button:hover {
          background: var(--gray-100);
          color: var(--gray-900);
        }

        .nav-links button.active {
          background: linear-gradient(135deg, var(--primary), var(--accent));
          color: white;
          box-shadow: 0 4px 16px rgba(37, 99, 235, 0.3);
        }

        .nav-links button.active::after {
          display: none;
        }

        .nav-icon {
          font-size: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: inherit;
        }

        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .mobile-menu-btn:hover {
          background: var(--gray-100);
        }

        .hamburger-icon {
          display: flex;
          flex-direction: column;
          gap: 5px;
          width: 24px;
          height: 20px;
          justify-content: center;
        }

        .hamburger-icon span {
          display: block;
          height: 3px;
          background: var(--gray-700);
          border-radius: 3px;
          transition: all 0.3s ease;
        }

        .hamburger-icon.open span:nth-child(1) {
          transform: translateY(8px) rotate(45deg);
        }

        .hamburger-icon.open span:nth-child(2) {
          opacity: 0;
        }

        .hamburger-icon.open span:nth-child(3) {
          transform: translateY(-8px) rotate(-45deg);
        }

        @media (max-width: 1024px) {
          .logo-text {
            font-size: 1.3rem;
          }
          .logo-highlight {
            font-size: 1.0rem;
          }
          .logo-wrapper {
            width: 40px;
            height: 40px;
          }
          .nav-links button {
            padding: 8px 16px;
            font-size: 0.9rem;
          }
          .nav-icon {
            font-size: 0.9rem;
          }
        }

        @media (max-width: 768px) {
          .navbar {
            padding: 12px 16px;
            border-radius: 0 0 16px 16px;
          }

          .logo-wrapper {
            width: 38px;
            height: 38px;
            border-radius: 10px;
          }

          .logo-text {
            font-size: 1.1rem;
          }
          .logo-highlight {
            font-size: 0.9rem;
          }

          .nav-links {
            display: none;
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(12px);
            flex-direction: column;
            padding: 16px;
            border-radius: 0 0 20px 20px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
            z-index: 1000;
            border-top: 2px solid var(--gray-100);
            gap: 8px;
          }

          .nav-links.open {
            display: flex;
            animation: slideDown 0.3s ease;
          }

          .nav-links button {
            padding: 12px 16px;
            width: 100%;
            justify-content: center;
            font-size: 1rem;
          }

          .nav-links button::after {
            display: none;
          }

          .mobile-menu-btn {
            display: block;
          }
        }

        @media (max-width: 480px) {
          .navbar {
            padding: 10px 12px;
          }

          .logo-wrapper {
            width: 32px;
            height: 32px;
            border-radius: 8px;
          }

          .logo-text {
            font-size: 0.95rem;
          }
          .logo-highlight {
            font-size: 0.75rem;
          }

          .logo:hover {
            transform: none;
          }

          .logo-wrapper:hover {
            transform: none;
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }

        .logo-wrapper {
          animation: pulse 3s ease-in-out infinite;
        }

        .logo-wrapper:hover {
          animation: none;
        }
      `}</style>
    </nav>
  );
};

export default Navbar;